import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  Link2, AlertTriangle, Lightbulb, ListChecks, Target,
  ChevronDown, ChevronRight, ShieldAlert,
} from 'lucide-react';
import { EntryActions } from '../ask/EntryActions';
import { useKeyFindings } from '../../contexts/KeyFindingsContext';
import type { ContextItem } from '../../contexts/AskSidebarContext';
import { useHighlightParam } from '../../hooks/useHighlightParam';
import { sanitizeEnumValue, enumDisplay } from '../../utils/displayUtils';
import { AnalysisErrorBanner, hasParseError } from './AnalysisErrorBanner';
import { MarkdownWithCitations } from '../citations/MarkdownWithCitations';
import { RedactionText } from '../redaction/RedactionText';
import { InlineCitedText } from '../citations/InlineCitedText';
import { CopyButton } from '../CopyButton';
import { formatFullPanel, formatAnalysisItem, formatIndexEvent, clean } from '../../utils/copyFormatters';
import { fromObjectiveSource, resolveDocumentId } from '../../types/citation';
import type { UnifiedCitation, ObjectiveSource } from '../../types/citation';

// --- Styling ---

const SEVERITY_BADGES: Record<string, string> = {
  high: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  medium: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
  low: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300',
  info: 'bg-gray-100 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300',
};

const STRENGTH_BADGES: Record<string, string> = {
  definite: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  established: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  probable: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
  possible: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  unlikely: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  excluded: 'bg-gray-100 dark:bg-gray-900/40 text-gray-600 dark:text-gray-400',
};

const HARM_BADGES: Record<string, string> = {
  death: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  severe_harm: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  moderate_harm: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
  minor_harm: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300',
  no_harm: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
};

const CONTRIBUTION_BADGES: Record<string, string> = {
  major: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  moderate: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
  minor: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300',
};

const PREVENTABILITY_BADGES: Record<string, string> = {
  definitely_preventable: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  probably_preventable: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
  possibly_preventable: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  not_preventable: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
};

const EVENT_TYPE_BADGES: Record<string, string> = {
  action: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300',
  omission: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  clinical_event: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  intervention: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
};

function sev(s: string | undefined): string { return sanitizeEnumValue(s) || 'info'; }
function sevBadge(s: string | undefined): string { return SEVERITY_BADGES[sev(s)] || SEVERITY_BADGES.info; }
function strengthBadge(s: string | undefined): string { return STRENGTH_BADGES[sev(s)] || SEVERITY_BADGES.info; }
function harmBadge(s: string | undefined): string { return HARM_BADGES[sev(s)] || SEVERITY_BADGES.info; }
function contribBadge(s: string | undefined): string { return CONTRIBUTION_BADGES[sev(s)] || SEVERITY_BADGES.info; }
function preventBadge(s: string | undefined): string { return PREVENTABILITY_BADGES[sev(s)] || SEVERITY_BADGES.info; }
function eventTypeBadge(s: string | undefined): string { return EVENT_TYPE_BADGES[sev(s)] || SEVERITY_BADGES.info; }

// --- Category config ---

interface CategoryConfig {
  key: string;
  label: string;
  icon: React.ReactNode;
}

const CATEGORIES: CategoryConfig[] = [
  { key: 'causalChains',       label: 'Causal Chains',        icon: <Link2 className="w-4 h-4 text-cyan-600" /> },
  { key: 'contributingFactors',label: 'Contributing Factors',  icon: <Target className="w-4 h-4 text-amber-600" /> },
  { key: 'confoundingFactors', label: 'Confounding Factors',   icon: <ShieldAlert className="w-4 h-4 text-purple-600" /> },
  { key: 'keyFindings',        label: 'Key Findings',          icon: <Lightbulb className="w-4 h-4 text-yellow-600" /> },
  { key: 'conclusions',        label: 'Conclusions',           icon: <AlertTriangle className="w-4 h-4 text-red-600" /> },
  { key: 'limitations',        label: 'Limitations',           icon: <ListChecks className="w-4 h-4 text-gray-600" /> },
];

function getItems(content: any, key: string): any[] {
  if (key === 'conclusions') return content.conclusions ? [content.conclusions] : [];
  return content[key] || [];
}

function getItemTitle(item: any, catKey: string): string {
  switch (catKey) {
    case 'causalChains': return item.description || '';
    case 'contributingFactors': return item.factor || '';
    case 'confoundingFactors': return item.factor || '';
    case 'keyFindings': return item.finding || '';
    case 'conclusions': return item.primaryCause || 'Conclusions';
    case 'limitations': return typeof item === 'string' ? item : '';
    default: return '';
  }
}

// --- Component ---

interface CausationAnalysisPanelProps {
  content: any;
  documents?: any[];
}

export const CausationAnalysisPanel: React.FC<CausationAnalysisPanelProps> = ({ content, documents = [] }) => {
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set(['causalChains']));
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [expandedChainEvents, setExpandedChainEvents] = useState<Set<string>>(new Set());
  const categoryRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const { isEventMarked } = useKeyFindings();
  const globalCitations = useMemo<UnifiedCitation[]>(() => {
    if (!content?.sources || !Array.isArray(content.sources)) return [];
    return content.sources
      .filter((s: any) => s && typeof s === 'object' && s.documentName)
      .map((s: any) => {
        const documentId = s.documentId || resolveDocumentId(s.documentName, documents);
        return fromObjectiveSource({ ...s, documentId } as ObjectiveSource);
      });
  }, [content, documents]);

  const toggleCat = useCallback((key: string) => {
    setExpandedCats(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  }, []);
  const toggleItem = useCallback((key: string) => {
    setExpandedItems(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  }, []);
  const toggleChainEvent = useCallback((key: string) => {
    setExpandedChainEvents(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  }, []);

  const expandAll = useCallback(() => setExpandedCats(new Set(CATEGORIES.map(c => c.key))), []);
  const collapseAll = useCallback(() => setExpandedCats(new Set()), []);

  const jumpTo = useCallback((key: string) => {
    const el = categoryRefs.current.get(key);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setExpandedCats(prev => new Set(prev).add(key)); }
  }, []);

  // Auto-expand and scroll to a highlighted finding from URL param
  const { highlightId, scrollToHighlighted } = useHighlightParam();
  useEffect(() => {
    if (!highlightId || !highlightId.startsWith('caus-')) return;
    // Extract category key from caus-{catKey}-{idx}
    const catKey = highlightId.replace(/^caus-/, '').replace(/-\d+$/, '');
    setExpandedCats(prev => new Set(prev).add(catKey));
    setExpandedItems(prev => new Set(prev).add(highlightId));
    requestAnimationFrame(() => scrollToHighlighted());
  }, [highlightId, scrollToHighlighted]);

  if (!content) return null;
  if (hasParseError(content)) return <AnalysisErrorBanner analysisType="Causation Analysis" />;

  const indexEvent = content.indexEvent;
  const conclusions = content.conclusions;
  const metadata = content.metadata;

  return (
    <div className="space-y-4">
      {/* ── Summary ── */}
      <div className="bg-gray-50 dark:bg-medical-800/50 border border-gray-200 dark:border-medical-700 rounded-lg p-4 space-y-3">
        <div className="text-sm text-gray-700 dark:text-medical-300 leading-relaxed">
          {conclusions?.overallCausationStrength && (
            <span className={`inline-block text-xs uppercase font-semibold px-2 py-0.5 rounded mr-2 ${strengthBadge(conclusions.overallCausationStrength)}`}>
              {enumDisplay(conclusions.overallCausationStrength)} causation
            </span>
          )}
          {conclusions?.preventability && (
            <span className={`inline-block text-xs uppercase font-semibold px-2 py-0.5 rounded mr-2 ${preventBadge(conclusions.preventability)}`}>
              {enumDisplay(conclusions.preventability)}
            </span>
          )}
          <MarkdownWithCitations content={content.summary || ''} citations={globalCitations} />
        </div>

        {/* Metadata stats */}
        {metadata && (
          <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-medical-400">
            {metadata.documentsAnalyzed && <span>{metadata.documentsAnalyzed} documents</span>}
            {metadata.causalChainsIdentified && <span>{metadata.causalChainsIdentified} causal chains</span>}
            {metadata.confidenceLevel && (
              <span className={`px-1.5 py-0.5 rounded ${sevBadge(metadata.confidenceLevel)}`}>
                {metadata.confidenceLevel} confidence
              </span>
            )}
          </div>
        )}

        {/* Jump-to nav */}
        <nav className="flex flex-wrap gap-1.5">
          {CATEGORIES.map(cat => {
            const items = getItems(content, cat.key);
            if (items.length === 0) return null;
            return (
              <button
                key={cat.key}
                onClick={() => jumpTo(cat.key)}
                className="text-xs px-2.5 py-1 rounded-full border border-gray-200 dark:border-medical-600
                  hover:bg-cyan-50 dark:hover:bg-cyan-900/30 hover:border-cyan-300 dark:hover:border-cyan-700
                  text-gray-600 dark:text-medical-400 transition-colors"
              >
                {cat.label} ({items.length})
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Index Event ── */}
      {indexEvent && (
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <h4 className="text-sm font-semibold text-red-800 dark:text-red-300">Index Event</h4>
            {indexEvent.severity && (
              <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${harmBadge(indexEvent.severity)}`}>
                {enumDisplay(indexEvent.severity)}
              </span>
            )}
            <CopyButton getText={() => formatIndexEvent(indexEvent)} />
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-medical-100"><MarkdownWithCitations content={indexEvent.event || indexEvent.description || ''} citations={globalCitations} /></div>
          {indexEvent.timestamp && <p className="text-xs text-gray-500 dark:text-medical-400"><RedactionText text={indexEvent.timestamp} /></p>}
          {indexEvent.description && indexEvent.event && <div className="text-sm text-gray-700 dark:text-medical-300"><MarkdownWithCitations content={indexEvent.description} citations={globalCitations} /></div>}
        </div>
      )}

      {/* ── Controls ── */}
      <div className="flex items-center justify-end gap-2 text-xs text-gray-500 dark:text-medical-400">
        <CopyButton
          getText={() => formatFullPanel('Causation Analysis', content.summary, CATEGORIES.map(cat => ({
            label: cat.label,
            items: getItems(content, cat.key),
            getTitle: (item: any) => getItemTitle(item, cat.key),
          })))}
          label="Copy all"
        />
        <span className="text-gray-300 dark:text-medical-600">|</span>
        <button onClick={expandAll} className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">Expand all</button>
        <span className="text-gray-300 dark:text-medical-600">|</span>
        <button onClick={collapseAll} className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">Collapse all</button>
      </div>

      {/* ── Category sections ── */}
      {CATEGORIES.map(cat => {
        const allItems = getItems(content, cat.key);
        if (allItems.length === 0) return null;

        const isExpanded = expandedCats.has(cat.key);

        return (
          <div
            key={cat.key}
            ref={el => { if (el) categoryRefs.current.set(cat.key, el); }}
            className="scroll-mt-4 bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg overflow-hidden"
          >
            <button
              onClick={() => toggleCat(cat.key)}
              className="sticky top-0 z-10 bg-white/95 dark:bg-medical-800/95 backdrop-blur-sm w-full flex items-center justify-between p-3.5 hover:bg-gray-50 dark:hover:bg-medical-700/50 transition-colors border-b border-gray-100 dark:border-medical-700"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-md bg-gray-100 dark:bg-medical-700 flex items-center justify-center flex-shrink-0">
                  {cat.icon}
                </div>
                <div className="text-left">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-medical-100">{cat.label}</h4>
                  <p className="text-xs text-gray-500 dark:text-medical-400">{allItems.length} {allItems.length === 1 ? 'item' : 'items'}</p>
                </div>
              </div>
              {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            </button>

            {isExpanded && (
              <div className="space-y-0.5">
                {allItems.map((item, idx) => {
                  const itemKey = `${cat.key}-${idx}`;
                  const isOpen = expandedItems.has(itemKey);
                  const title = getItemTitle(item, cat.key);

                  // Limitations are simple strings
                  if (cat.key === 'limitations') {
                    return (
                      <div key={itemKey} className="px-4 py-2.5 text-sm text-gray-700 dark:text-medical-300 flex items-start gap-2">
                        <span className="text-gray-400 mt-0.5">•</span>
                        <span><RedactionText text={typeof item === 'string' ? item : JSON.stringify(item)} /></span>
                      </div>
                    );
                  }

                  const causItemMarked = isEventMarked(`caus-${cat.key}-${idx}`);
                  return (
                    <div key={itemKey} data-event-id={`caus-${cat.key}-${idx}`} className={`group transition-all duration-150 rounded-lg border ${causItemMarked ? 'border-amber-300 dark:border-amber-600 shadow-sm' : 'border-transparent'} ${isOpen ? 'bg-gray-50/50 dark:bg-medical-700/20' : ''}`}>
                      <button
                        onClick={() => toggleItem(itemKey)}
                        className="w-full flex items-start gap-2 p-3 text-left hover:bg-gray-50 dark:hover:bg-medical-700/30 transition-colors rounded-lg"
                      >
                        {cat.key === 'causalChains' && item.causalAssessment?.overallStrength && (
                          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${strengthBadge(item.causalAssessment.overallStrength)}`}>
                            {item.causalAssessment.overallStrength}
                          </span>
                        )}
                        {cat.key === 'contributingFactors' && item.contribution && (
                          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${contribBadge(item.contribution)}`}>
                            {item.contribution}
                          </span>
                        )}
                        {cat.key === 'confoundingFactors' && item.significance && (
                          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${sevBadge(item.significance)}`}>
                            {item.significance}
                          </span>
                        )}
                        {cat.key === 'keyFindings' && item.strength && (
                          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${strengthBadge(item.strength)}`}>
                            {item.strength}
                          </span>
                        )}
                        <span className={`text-sm text-gray-900 dark:text-medical-100 flex-1 ${isOpen ? 'font-medium' : ''}`}>
                          <InlineCitedText text={title} citations={globalCitations} />
                        </span>
                        <EntryActions
                          compact
                          item={{
                            id: `caus-${cat.key}-${idx}`,
                            type: 'causation' as ContextItem['type'],
                            summary: title.slice(0, 80),
                            source: `Causation — ${cat.label}`,
                            data: item,
                          }}
                          onCopy={() => formatAnalysisItem(item, title)}
                        />
                        {isOpen
                          ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-1" />
                          : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-1" />}
                      </button>

                      {isOpen && (
                        <div className="px-3 pb-3 pt-0 ml-[calc(1.5rem+0.5rem)] space-y-2 text-sm text-gray-700 dark:text-medical-300">
                          {cat.key === 'causalChains' && renderCausalChain(item, idx, expandedChainEvents, toggleChainEvent, globalCitations)}
                          {cat.key === 'contributingFactors' && renderContributing(item, globalCitations)}
                          {cat.key === 'confoundingFactors' && renderConfounding(item, globalCitations)}
                          {cat.key === 'keyFindings' && renderKeyFinding(item, globalCitations)}
                          {cat.key === 'conclusions' && renderConclusions(item, globalCitations)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// --- Detail renderers ---

function renderCausalChain(
  chain: any,
  chainIdx: number,
  expandedEvents: Set<string>,
  toggleEvent: (key: string) => void,
  globalCitations: UnifiedCitation[] = [],
): React.ReactNode {
  const events = chain.events || [];
  const assessment = chain.causalAssessment;

  return (
    <>
      {/* Events timeline */}
      {events.length > 0 && (
        <div className="relative">
          <p className="text-xs font-medium text-gray-500 dark:text-medical-400 mb-2">Event Chain ({events.length} events)</p>
          <div className="space-y-0">
            {events.map((evt: any, evtIdx: number) => {
              const evtKey = `chain-${chainIdx}-evt-${evtIdx}`;
              const evtOpen = expandedEvents.has(evtKey);
              const isLast = evtIdx === events.length - 1;
              return (
                <div key={evtKey} className="flex">
                  {/* Timeline connector */}
                  <div className="flex flex-col items-center mr-3 flex-shrink-0">
                    <div className="w-2.5 h-2.5 rounded-full bg-cyan-500 border-2 border-white dark:border-medical-800 mt-1.5 z-10" />
                    {!isLast && <div className="w-0.5 flex-1 bg-cyan-200 dark:bg-cyan-800" />}
                  </div>
                  <div className={`flex-1 pb-3 ${!isLast ? 'border-b border-transparent' : ''}`}>
                    <button
                      onClick={() => toggleEvent(evtKey)}
                      className="w-full text-left flex items-start gap-1.5"
                    >
                      {evt.type && (
                        <span className={`text-[10px] uppercase font-bold px-1 py-0.5 rounded flex-shrink-0 ${eventTypeBadge(evt.type)}`}>
                          {enumDisplay(evt.type)}
                        </span>
                      )}
                      <span className="text-sm flex-1"><RedactionText text={evt.description} /></span>
                      {evtOpen ? <ChevronDown className="w-3 h-3 text-gray-400 mt-1" /> : <ChevronRight className="w-3 h-3 text-gray-400 mt-1" />}
                    </button>
                    {evtOpen && (
                      <div className="mt-1 ml-1 space-y-1 text-xs">
                        {evt.timestamp && <p className="text-gray-500 dark:text-medical-400"><RedactionText text={evt.timestamp} /></p>}
                        {evt.actor && <p className="text-gray-500 dark:text-medical-400">Actor: <RedactionText text={evt.actor} /></p>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Causal assessment */}
      {assessment && (
        <div className="p-2.5 bg-gray-50 dark:bg-medical-700/30 rounded-lg border border-gray-200 dark:border-medical-600 space-y-2">
          <p className="text-xs font-medium text-gray-500 dark:text-medical-400">Causal Assessment</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {assessment.factualCausation && (
              <div>
                <span className="text-gray-500 dark:text-medical-400">Factual: </span>
                <span className={`inline-block px-1.5 py-0.5 rounded font-bold uppercase text-[10px] ${strengthBadge(assessment.factualCausation)}`}>
                  {assessment.factualCausation}
                </span>
              </div>
            )}
            {assessment.legalCausation && (
              <div>
                <span className="text-gray-500 dark:text-medical-400">Legal: </span>
                <span className={`inline-block px-1.5 py-0.5 rounded font-bold uppercase text-[10px] ${strengthBadge(assessment.legalCausation)}`}>
                  {assessment.legalCausation}
                </span>
              </div>
            )}
            {assessment.medicalCausation && (
              <div>
                <span className="text-gray-500 dark:text-medical-400">Medical: </span>
                <span className={`inline-block px-1.5 py-0.5 rounded font-bold uppercase text-[10px] ${strengthBadge(assessment.medicalCausation)}`}>
                  {assessment.medicalCausation}
                </span>
              </div>
            )}
            {assessment.overallStrength && (
              <div>
                <span className="text-gray-500 dark:text-medical-400">Overall: </span>
                <span className={`inline-block px-1.5 py-0.5 rounded font-bold uppercase text-[10px] ${strengthBadge(assessment.overallStrength)}`}>
                  {assessment.overallStrength}
                </span>
              </div>
            )}
          </div>
          {assessment.confidence && <p className="text-xs text-gray-500 dark:text-medical-400">Confidence: {assessment.confidence}</p>}
        </div>
      )}

      {chain.mechanism && <div><span className="font-medium text-gray-900 dark:text-medical-100">Mechanism: </span><MarkdownWithCitations content={chain.mechanism} citations={globalCitations} /></div>}
      {chain.evidence?.length > 0 && (
        <div>
          <span className="text-xs font-medium text-gray-500 dark:text-medical-400">Evidence:</span>
          <ul className="text-xs list-disc list-inside text-gray-500 dark:text-medical-400 mt-0.5">
            {chain.evidence.map((e: string, i: number) => <li key={i}><MarkdownWithCitations content={e} citations={globalCitations} /></li>)}
          </ul>
        </div>
      )}
      {chain.alternativeExplanations?.length > 0 && (
        <div>
          <span className="text-xs font-medium text-gray-500 dark:text-medical-400">Alternative explanations:</span>
          <ul className="text-xs list-disc list-inside text-gray-500 dark:text-medical-400 mt-0.5">
            {chain.alternativeExplanations.map((e: string, i: number) => <li key={i}><MarkdownWithCitations content={e} citations={globalCitations} /></li>)}
          </ul>
        </div>
      )}
    </>
  );
}

function renderContributing(item: any, globalCitations: UnifiedCitation[] = []): React.ReactNode {
  return (
    <>
      {item.explanation && <MarkdownWithCitations content={item.explanation} citations={globalCitations} />}
      {item.preventable !== undefined && (
        <p className="text-xs">
          <span className={`inline-block px-1.5 py-0.5 rounded font-bold uppercase text-[10px] ${
            item.preventable ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
          }`}>
            {item.preventable ? 'Preventable' : 'Not preventable'}
          </span>
        </p>
      )}
    </>
  );
}

function renderConfounding(item: any, globalCitations: UnifiedCitation[] = []): React.ReactNode {
  return (
    <>
      {item.impact && <MarkdownWithCitations content={item.impact} citations={globalCitations} />}
    </>
  );
}

function renderKeyFinding(item: any, globalCitations: UnifiedCitation[] = []): React.ReactNode {
  return (
    <>
      {item.significance && <MarkdownWithCitations content={item.significance} citations={globalCitations} />}
      {item.evidence?.length > 0 && (
        <ul className="text-xs text-gray-500 dark:text-medical-400 list-disc list-inside">
          {item.evidence.map((e: string, i: number) => <li key={i}><MarkdownWithCitations content={e} citations={globalCitations} /></li>)}
        </ul>
      )}
    </>
  );
}

function renderConclusions(item: any, globalCitations: UnifiedCitation[] = []): React.ReactNode {
  return (
    <>
      {item.primaryCause && <div><span className="font-medium text-gray-900 dark:text-medical-100">Primary cause: </span><MarkdownWithCitations content={item.primaryCause} citations={globalCitations} /></div>}
      {item.contributoryCauses?.length > 0 && (
        <div>
          <span className="text-xs font-medium text-gray-500 dark:text-medical-400">Contributory causes:</span>
          <ul className="text-xs list-disc list-inside text-gray-600 dark:text-medical-400 mt-0.5">
            {item.contributoryCauses.map((c: string, i: number) => <li key={i}><MarkdownWithCitations content={c} citations={globalCitations} /></li>)}
          </ul>
        </div>
      )}
      {item.overallCausationStrength && (
        <p className="text-xs">
          Overall strength:{' '}
          <span className={`inline-block px-1.5 py-0.5 rounded font-bold uppercase text-[10px] ${strengthBadge(item.overallCausationStrength)}`}>
            {enumDisplay(item.overallCausationStrength)}
          </span>
        </p>
      )}
      {item.preventability && (
        <p className="text-xs">
          Preventability:{' '}
          <span className={`inline-block px-1.5 py-0.5 rounded font-bold uppercase text-[10px] ${preventBadge(item.preventability)}`}>
            {enumDisplay(item.preventability)}
          </span>
        </p>
      )}
      {item.confidence && <p className="text-xs text-gray-500 dark:text-medical-400">Confidence: {item.confidence}</p>}
    </>
  );
}
